<script setup>
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import WootLabel from 'dashboard/components-next/label/Label.vue';
import LaisRunStatusPanel from 'dashboard/components-next/LaisRunStatusPanel.vue';
import LaisPayloadViewer from 'dashboard/components-next/LaisPayloadViewer.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

import AutomationExecutionActionDrilldown from './AutomationExecutionActionDrilldown.vue';
import AutomationExecutionTimeline from './AutomationExecutionTimeline.vue';
import { useAutomationOperationsFormatters } from './composables/useAutomationOperationsFormatters';
import { REDACTED_KEYS } from './utils/automationOperationsConstants';
import {
  buildExecutionRunStatus,
  executionPayloadOf,
  executionPolicyRowsOf,
  executionVersionRowsOf,
} from './utils/automationOperationsFormatters';

const props = defineProps({
  detail: { type: Object, required: true },
  actionRef: { type: String, default: '' },
});

defineEmits(['selectAction']);

const { formatStatus } = useAutomationOperationsFormatters();

const executionActions = computed(() => props.detail?.actions || []);
const executionTimeline = computed(() => props.detail?.timeline || []);
const executionPayload = computed(() =>
  executionPayloadOf(props.detail?.execution)
);
const executionRunStatus = computed(() =>
  buildExecutionRunStatus(props.detail?.execution)
);
const executionVersionRows = computed(() =>
  executionVersionRowsOf(props.detail?.version)
);
const executionPolicyRows = computed(() =>
  executionPolicyRowsOf(props.detail?.execution)
);

const selectedExecutionAction = computed(() => {
  const actionRef = props.actionRef.trim();
  return (
    executionActions.value.find(action => action.action_ref === actionRef) ||
    executionActions.value[0] ||
    null
  );
});
</script>

<template>
  <div
    class="mt-4 grid gap-4 border border-n-weak bg-n-surface-1 p-4 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]"
  >
    <div class="min-w-0">
      <div class="mb-3 flex min-w-0 items-center justify-between gap-3">
        <div class="flex min-w-0 flex-col gap-1">
          <span class="truncate text-heading-3 text-n-slate-12">
            {{
              $t('AUTOMATION.OPERATIONS.EXECUTION.TITLE', {
                id: detail.execution?.id,
              })
            }}
          </span>
          <span class="truncate text-body-main text-n-slate-11">
            {{ detail.rule?.name || '-' }}
          </span>
        </div>
        <LaisStatusBadge
          :label="formatStatus(detail.execution?.status)"
          :tone="detail.execution?.status || 'unknown'"
        />
      </div>
      <LaisRunStatusPanel
        v-if="executionRunStatus"
        v-bind="executionRunStatus"
        class="mb-3"
      />
      <dl class="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-3 gap-y-2">
        <dt class="text-body-main text-n-slate-11">
          {{ $t('AUTOMATION.OPERATIONS.EXECUTION.EVENT') }}
        </dt>
        <dd class="min-w-0 truncate text-body-main text-n-slate-12">
          {{ detail.execution?.event_name || '-' }}
        </dd>
        <dt class="text-body-main text-n-slate-11">
          {{ $t('AUTOMATION.OPERATIONS.EXECUTION.DOMAIN_EVENT') }}
        </dt>
        <dd class="min-w-0 truncate text-body-main text-n-slate-12">
          {{ detail.execution?.domain_event_id || '-' }}
        </dd>
        <dt class="text-body-main text-n-slate-11">
          {{ $t('AUTOMATION.OPERATIONS.EXECUTION.CORRELATION') }}
        </dt>
        <dd class="min-w-0 break-all text-body-main text-n-slate-12">
          {{ detail.execution?.correlation_id || '-' }}
        </dd>
        <dt class="text-body-main text-n-slate-11">
          {{ $t('AUTOMATION.OPERATIONS.EXECUTION.ACTIONS') }}
        </dt>
        <dd class="tabular-nums text-body-main text-n-slate-12">
          {{
            $t('AUTOMATION.OPERATIONS.EXECUTION.ACTION_PROGRESS', {
              completed: detail.execution?.actions_completed || 0,
              planned: detail.execution?.actions_planned || 0,
            })
          }}
        </dd>
        <template
          v-for="[label, value] in executionVersionRows"
          :key="`version:${label}`"
        >
          <dt class="text-body-main text-n-slate-11">
            {{ formatStatus(label) }}
          </dt>
          <dd class="min-w-0 break-all text-body-main text-n-slate-12">
            {{ value || '-' }}
          </dd>
        </template>
        <template
          v-for="[label, value] in executionPolicyRows"
          :key="`policy:${label}`"
        >
          <dt class="text-body-main text-n-slate-11">
            {{ formatStatus(label) }}
          </dt>
          <dd class="min-w-0 break-all text-body-main text-n-slate-12">
            {{ value || '-' }}
          </dd>
        </template>
      </dl>

      <div class="mt-4 flex flex-col divide-y divide-n-weak">
        <div
          v-for="action in executionActions.slice(0, 8)"
          :key="action.action_ref"
          class="grid gap-1 py-2 first:pt-0 last:pb-0"
        >
          <div class="flex min-w-0 items-center justify-between gap-2">
            <span class="truncate text-body-main text-n-slate-12">
              {{ action.action_name || action.action_ref }}
            </span>
            <span class="text-body-main text-n-slate-11">
              {{ action.action_ref }}
            </span>
            <Button
              v-tooltip.top="$t('AUTOMATION.OPERATIONS.EXECUTION.DETAIL')"
              icon="i-lucide-list-tree"
              color="slate"
              variant="ghost"
              size="sm"
              :data-test="`automation-action-detail-${action.action_ref}`"
              @click="$emit('selectAction', action)"
            />
          </div>
          <div
            class="flex flex-wrap gap-x-3 gap-y-1 text-body-main text-n-slate-11"
          >
            <span class="tabular-nums">
              {{
                $t('AUTOMATION.OPERATIONS.EXECUTION.ATTEMPTS', {
                  count: action.summary?.attempt_count || 0,
                })
              }}
            </span>
            <span class="tabular-nums">
              {{
                $t('AUTOMATION.OPERATIONS.EXECUTION.RECEIPTS', {
                  count: action.summary?.receipt_count || 0,
                })
              }}
            </span>
            <span
              v-for="[result, count] in Object.entries(
                action.summary?.results || {}
              )"
              :key="`${action.action_ref}:${result}`"
              class="tabular-nums"
            >
              {{ formatStatus(result) }} {{ count }}
            </span>
          </div>
        </div>
      </div>

      <AutomationExecutionActionDrilldown
        v-if="selectedExecutionAction"
        :action="selectedExecutionAction"
      />

      <LaisPayloadViewer
        v-if="executionPayload"
        :payload="executionPayload"
        :title="$t('AUTOMATION.OPERATIONS.EXECUTION.PAYLOAD_TITLE')"
        :redacted-keys="REDACTED_KEYS"
        class="mt-4"
      />
    </div>

    <AutomationExecutionTimeline :timeline="executionTimeline" />
  </div>
</template>
